import styled from '@emotion/styled';
import { Button, message, Tag } from 'antd';
import dayjs from 'lib/dayjs';
import { useTranslate } from '@tolgee/react';
import { useAtomValue } from 'jotai';
import copyText from 'utils/copyText';
import { Between } from 'views/Conversation/Sider/TradeRefund';
import TradeProduct from 'views/Conversation/Sider/TradeProduct';
import { useTradeContext } from 'views/Conversation/Sider/StateContext';
import ImageSlide from './ImageSlide';
import { StarRate } from './Profile';
import { SystemMessage } from './ReplyDetail';
import Translation from './Translation';
import { useGlobalReview } from '../hooks/useNewReview';
import { useReviewContext } from '../Context';
import { currentReviewProfileState } from '../hooks/useReview';
import { ProductTagList, TagProduct } from 'beisi/TagProduct';
import { Fragment, useMemo } from 'react';
import { filter, map } from 'lodash-es';
import { useTranslateReport } from 'hooks/useTranslateReport';

export const ReviewMessage = styled.div`
  width: 100%;
  padding: 14px;
  border-radius: 4px;
  background: #fff;
  white-space: pre-line;
`;

export const BetweenStart = styled(Between)`
  align-items: center;
`;

export const Operation = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const UIProduct = styled(TradeProduct)`
  border-bottom: 1px solid #efefef;
  margin-bottom: 8px;
  padding: 8px 0;
`;

export interface BuyerReviewDetailProps {
  reviewDetail: Review.ReviewDetail;
  isMainReview?: boolean;
  isBuyerFirstReview?: boolean;
  isTikTok?: boolean;
}

function BuyerReviewDetail(props: BuyerReviewDetailProps) {
  const { reviewDetail, isMainReview = false, isBuyerFirstReview = true, isTikTok = false } = props;

  const { id, commentCreateTime, ratingStar, buyerComment, transBuyerComment } = reviewDetail;

  const { t } = useTranslate();

  const { changeFiltePartial } = useGlobalReview();

  const { conversation } = useReviewContext();

  const [trade] = useTradeContext();

  const currentReview = useAtomValue(currentReviewProfileState);

  const translateReport = useTranslateReport();

  const [commentProducts, spuName] = useMemo(() => {
    let spuName = '';
    const match = map(trade?.items, (item) => {
      const { itemComments, itemName } = item;
      const hasThisComment = itemComments?.some((v) => {
        if (v.commentId === currentReview?.commentId) {
          spuName = itemName;
          return true;
        }
        return false;
      });
      return hasThisComment ? item : undefined!;
    });
    return [filter(match, Boolean), spuName] as const;
  }, [trade, currentReview]);

  const ComentProducts = commentProducts?.map((item, index) => {
    return (
      <Fragment key={`${item.itemId}-${index}`}>
        <UIProduct
          product={item}
          conversation={conversation}
          productIndex={index}
          showReturn
          showComment={false}
          externalShopId={trade.externalShopId}
        />
        <ProductTagList productData={item} trade={trade} />
      </Fragment>
    );
  });

  const UIFindProduct =
    (isTikTok && isMainReview && spuName) || (!isTikTok && spuName) ? (
      <Button type="link" onClick={() => changeFiltePartial({ spuName })}>
        {t('review.筛选该商品评价')}
      </Button>
    ) : null;

  return (
    <>
      <SystemMessage>
        {`${dayjs(parseInt(commentCreateTime, 10)).format('YYYY-MM-DD HH:mm:ss')} ${
          isBuyerFirstReview
            ? t('review.用户发布评价')
            : isTikTok
            ? t('用户追评')
            : t('用户修改评价')
        }`}
      </SystemMessage>
      <ReviewMessage>
        {buyerComment ? (
          <Translation
            originText={buyerComment}
            translationMap={transBuyerComment}
            afterTranslation={(lang, text) => {
              translateReport(
                {
                  id,
                  flag: isMainReview ? 1 : 2,
                  transComment: {
                    [lang]: text,
                  },
                },
                'Review',
              );
            }}
          >
            {({ text, operation, displayTranslation }) => {
              return (
                <>
                  {ComentProducts}
                  <TagProduct
                    productData={commentProducts}
                    border={['bottom']}
                    trade={trade}
                    className="pb-[8px]"
                    conversation={conversation}
                    sourceId={reviewDetail.id}
                  />
                  <BetweenStart>
                    <div className="flex items-center gap-1">
                      {isTikTok && !isMainReview ? (
                        <Tag color="blue">{t('追评')}</Tag>
                      ) : (
                        <StarRate value={ratingStar} disabled />
                      )}
                    </div>
                    <Operation>
                      {UIFindProduct}
                      {displayTranslation && (
                        <Button
                          type="link"
                          onClick={() => {
                            copyText(text)
                              .then(() => {
                                message.success(t('trade.复制成功'));
                              })
                              .catch(() => {
                                message.error(t('trade.复制失败'));
                              });
                          }}
                        >
                          {t('review.复制评价')}
                        </Button>
                      )}
                      {operation}
                    </Operation>
                  </BetweenStart>
                  <div>{text}</div>
                </>
              );
            }}
          </Translation>
        ) : (
          <>
            {ComentProducts}
            <BetweenStart>
              <StarRate value={ratingStar} disabled />
              <Operation>{UIFindProduct}</Operation>
            </BetweenStart>
          </>
        )}
        <ImageSlide reviewDetail={reviewDetail} />
      </ReviewMessage>
    </>
  );
}

export default BuyerReviewDetail;
